import React, { useState, useEffect } from 'react';
import { 
    X, Share2, MessageCircle, Linkedin, Send, 
    Check, Loader, AlertCircle, TrendingUp, Award,
    Sparkles, Coins, Info, CheckCircle, Copy, Clock, Shield
} from 'lucide-react';

import './ShareSuccessModal.css';

const ShareSuccessModal = ({ 
    isOpen, 
    onClose, 
    prediction,
    userToken 
}) => {
    // 🔍 DEBUG: Log every render
    console.log('🎭 ShareSuccessModal rendered:', { 
        isOpen, 
        hasPrediction: !!prediction,
        predictionId: prediction?.id,
        predictionText: prediction?.prediction_text?.substring(0, 50),
        hasToken: !!userToken 
    });
    const [shareStatus, setShareStatus] = useState({
        x: { shared: false, loading: false },
        linkedin: { shared: false, loading: false }
    });
    const [dailyShares, setDailyShares] = useState(0);
    const [rewardEarned, setRewardEarned] = useState(0);
    const [error, setError] = useState('');
    const [cooldown, setCooldown] = useState(0);
    const [copied, setCopied] = useState(false);
    

    const DAILY_LIMIT = 5;
    const REWARD_PER_SHARE = 100;
    const COOLDOWN_SECONDS = 60;

    useEffect(() => {
        if (isOpen) {
            fetchDailyShareCount();
        }
    }, [isOpen]);

    useEffect(() => {
        if (cooldown > 0) {
            const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [cooldown]);
    const handleCopyPrediction = () => {
        const textToCopy = `${prediction.prediction_text}\n\nCategory: ${prediction.category}\n\n📊 Shared via @Trendphile-official\n🔗 https://trendphile.com/`;
        
        navigator.clipboard.writeText(textToCopy).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 3000);
        }).catch(err => {
            console.error('Failed to copy:', err);
            setError('Failed to copy text');
        });
    };
    
    
    const fetchDailyShareCount = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/social-share/daily-count`, {
                headers: {
                    'Authorization': `Bearer ${userToken}`,
                    'Content-Type': 'application/json'
                }
            });
            
            // Check if response is JSON
            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                console.error('Expected JSON response but got:', contentType);
                throw new Error('Server returned non-JSON response');
            }
            
            const data = await response.json();
            setDailyShares(data.count || 0);
        } catch (error) {
            console.error('Failed to fetch share count:', error);
            setError('Unable to load share status. Please try again.');
        }
    };

    const recordShare = async (platform) => {
        if (cooldown > 0) {
            setError(`Please wait ${cooldown} seconds before sharing again`); // Icon handled by cooldown-alert
            return false;
        }

        if (dailyShares >= DAILY_LIMIT) {
            setError(`Daily limit reached! You've earned the maximum today. Come back tomorrow for more rewards!`);
            return false;
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/social-share/record`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${userToken}`
                },
                body: JSON.stringify({
                    predictionId: prediction.id,
                    platform: platform
                })
            });

            // Check if response is JSON before parsing
            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                console.error('Expected JSON response but got:', contentType);
                console.error('Response status:', response.status);
                throw new Error('Server returned an unexpected response. Please try again.');
            }

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to record share');
            }

            // Update state
            setDailyShares(prev => prev + 1);
            setRewardEarned(prev => prev + REWARD_PER_SHARE);
            setCooldown(COOLDOWN_SECONDS);
            setError('');

            return true;
        } catch (error) {
            setError(error.message);
            return false;
        }
    };

    const handleShare = async (platform) => {
        // Prevent spam clicking
        if (shareStatus[platform].loading || shareStatus[platform].shared) {
            return;
        }

        setShareStatus(prev => ({
            ...prev,
            [platform]: { ...prev[platform], loading: true }
        }));

        // Generate share URL
        const shareUrl = generateShareUrl(platform);
        
        // Open share window
        window.open(shareUrl, '_blank', 'width=600,height=400');

        // Record the share
        const success = await recordShare(platform);

        setShareStatus(prev => ({
            ...prev,
            [platform]: { 
                shared: success, 
                loading: false 
            }
        }));
    };

    const generateShareUrl = (platform) => {
        const baseUrl = 'https://trendphile.com';
        const predictionUrl = `${baseUrl}/prediction/${prediction.id}`;
        const text = `${prediction.prediction_text.substring(0, 180)}... | Shared via @Trendphile`;
        
        switch (platform) {
            case 'x':
                const twitterText = `${prediction.prediction_text.substring(0, 180)}...\n\n@TrendphileAI #Prediction #FutureInsights`;          
                return `https://twitter.com/intent/tweet?text=${encodeURIComponent(twitterText)}&url=${encodeURIComponent(predictionUrl)}`;
            
                case 'linkedin':
                    // LinkedIn doesn't support pre-filled text, but we can include URL
                    return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(predictionUrl)}&summary=${encodeURIComponent('Check out this prediction! Remember to mention @Trendphile-official in your post!')}`;
                
            // Telegram removed - messenger, not social media
            default:
                return predictionUrl;
        }
    };

    if (!isOpen) {
        console.log('❌ Modal not open - returning null');
        return null;
    }

    if (!prediction) {
        console.log('❌ No prediction data - returning null');
        return null;
    }

    console.log('✅ Rendering modal content');

    const sharesRemaining = DAILY_LIMIT - dailyShares;

    return (
        <div className="share-modal-overlay" onClick={onClose}>
            <div className="share-modal" onClick={(e) => e.stopPropagation()}>
                {/* Animated Background Elements */}
                <div className="modal-bg-effect"></div>
                <div className="modal-glow"></div>

                {/* Header */}
                <div className="share-modal-header">
                    <div className="share-modal-title">
                        <div className="title-icon-wrapper">
                            <TrendingUp className="title-icon" size={28} />
                            <Sparkles className="sparkle-icon" size={16} />
                        </div>
                        <div className="title-text">
                            <h3>Prediction Published!</h3>
                            <p>Share and earn TPHILE tokens</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="share-modal-close">
                        <X size={20} />
                    </button>
                </div>
                {/* ⚠️ WARNING BANNER */}
<div className="share-warning-banner">
    <div className="warning-icon">
        <AlertCircle size={20} />
    </div>
    <div className="warning-content">
    <h4><Info size={16} /> Sharing Rules</h4>
    <ul>
        <li>
            <CheckCircle size={14} />
            <span>You MUST mention <strong>@Trendphile-official</strong> (LinkedIn) or <strong>@TrendphileAI</strong> (X/Twitter)</span>
        </li>
        <li>
            <Copy size={14} />
            <span>Copy and paste your prediction in your post</span>
        </li>
        <li>
            <Clock size={14} />
            <span>We verify all posts within 48 hours</span>
        </li>
        <li>
            <Shield size={14} />
            <span>Gaming the system = <strong>-500 FTAP penalty</strong> + account warning</span>
        </li>
    </ul>
</div>

</div>

{/* Prediction Preview Card */}
<div className="prediction-preview-card">
    <div className="preview-header">
        <div className="preview-icon">
            <Award size={20} />
        </div>
        <button 
            className={`copy-prediction-btn ${copied ? 'copied' : ''}`}
            onClick={handleCopyPrediction}
        >
            {copied ? (
                <>
                    <Check size={16} />
                    <span>Copied!</span>
                </>
            ) : (
                <>
                    <Share2 size={16} />
                    <span>Copy Prediction</span>
                </>
            )}
        </button>
    </div>

                    <div className="preview-content">
                        <p className="preview-text">
                            "{prediction.prediction_text.substring(0, 120)}
                            {prediction.prediction_text.length > 120 && '..."'}
                        </p>
                        <div className="preview-meta">
                            <span className="category-tag">
                                {prediction.category}
                            </span>
                            {prediction.reliability_score && (
                                <span className="quality-tag">
                                    <Award size={12} />
                                    {Math.round(prediction.reliability_score)}% Quality
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Rewards Section */}
                <div className="rewards-showcase">
                    <div className="reward-main-card">
                        <div className="reward-icon-wrapper">
                            <Coins className="coin-icon" size={32} />
                            <div className="coin-glow"></div>
                        </div>
                        <div className="reward-info">
                            <h4 className="reward-amount">
                                {REWARD_PER_SHARE} <span className="token-symbol">TPHILE</span>
                            </h4>
                            <p className="reward-subtitle">per share</p>
                        </div>
                    </div>

                    <div className="reward-stats">
                        <div className="stat-item">
                            <span className="stat-label">Today's Shares</span>
                            <span className="stat-value">{dailyShares}/{DAILY_LIMIT}</span>
                        </div>
                        <div className="stat-divider"></div>
                        <div className="stat-item">
                            <span className="stat-label">Remaining</span>
                            <span className={`stat-value ${sharesRemaining === 0 ? 'depleted' : ''}`}>
                                {sharesRemaining}
                            </span>
                        </div>
                    </div>

                    {rewardEarned > 0 && (
                        <div className="reward-earned-banner">
                            <Sparkles size={18} />
                            <span>You've earned <strong>{rewardEarned} TPHILE</strong> today!</span>
                            <Sparkles size={18} />
                        </div>
                    )}
                </div>

                {/* Error Alert */}
                {error && (
                    <div className="share-alert error-alert">
                        <AlertCircle size={18} />
                        <span>{error}</span>
                    </div>
                )}

                {/* Cooldown Indicator */}
                {cooldown > 0 && (
                    <div className="share-alert cooldown-alert">
                        <Loader size={18} className="spinning" />
                        <span>Next share available in <strong>{cooldown}s</strong></span>
                    </div>
                )}

                {/* Share Platform Buttons */}
                <div className="share-platforms">
                    <h4 className="platforms-title">Choose Platform to Share</h4>
                    
                    {/* Friendly Follow Reminder - Non-blocking info */}
                    <div className="share-alert info-alert">
                        <Info size={18} />
                        <span>Follow us on <strong>@TrendphileAI</strong> (Twitter) and <strong>@Trendphile-official</strong> (LinkedIn) to stay updated!</span>
                    </div>
                    
                    <button 
                        className={`platform-btn x-platform ${shareStatus.x.shared ? 'shared' : ''}`}
                        onClick={() => handleShare('x')}
                        disabled={shareStatus.x.loading || shareStatus.x.shared || cooldown > 0}
                    >
                        <div className="platform-content">
                            <div className="platform-icon-wrapper">
                                {shareStatus.x.loading ? (
                                    <Loader size={24} className="spinning" />
                                ) : shareStatus.x.shared ? (
                                    <Check size={24} />
                                ) : (
                                    <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
</svg>
                                )}
                            </div>
                            <div className="platform-info">
                                <span className="platform-name">X (Twitter)</span>
                                <span className="platform-desc">
                                    {shareStatus.x.shared ? 'Shared successfully!' : 'Share with your followers'}
                                </span>
                            </div>
                        </div>
                        {!shareStatus.x.shared && (
                            <div className="platform-reward">
                                +{REWARD_PER_SHARE} TPHILE
                            </div>
                        )}
                    </button>

                    <button 
                        className={`platform-btn linkedin-platform ${shareStatus.linkedin.shared ? 'shared' : ''}`}
                        onClick={() => handleShare('linkedin')}
                        disabled={shareStatus.linkedin.loading || shareStatus.linkedin.shared || cooldown > 0}
                    >
                        <div className="platform-content">
                            <div className="platform-icon-wrapper">
                                {shareStatus.linkedin.loading ? (
                                    <Loader size={24} className="spinning" />
                                ) : shareStatus.linkedin.shared ? (
                                    <Check size={24} />
                                ) : (
                                    <Linkedin size={24} />
                                )}
                            </div>
                            <div className="platform-info">
                                <span className="platform-name">LinkedIn</span>
                                <span className="platform-desc">
                                    {shareStatus.linkedin.shared ? 'Shared successfully!' : 'Share with professionals'}
                                </span>
                            </div>
                        </div>
                        {!shareStatus.linkedin.shared && (
                            <div className="platform-reward">
                                +{REWARD_PER_SHARE} TPHILE
                            </div>
                        )}
                    </button>

                    {/* Telegram button removed - messenger, not social media */}
                </div>

                {/* Footer Actions */}
                <div className="modal-footer">
                    <button onClick={onClose} className="skip-button">
                        <span>Skip for now</span>
                    </button>
                    <div className="footer-note">
    <Info size={14} />
    <span>You can share from your profile later</span>
</div>

                </div>
            </div>
        </div>
    );
};

export default ShareSuccessModal;
